/* ============================================================================
   File a claim — the entry point.
   ----------------------------------------------------------------------------
   This page used to ask for a "claim form code" and then navigate to
   /file-a-claim/<code>. That was a step invented for the platform's
   convenience, not the shopper's: nobody has a form code, and the page offered
   no way to find one. Someone whose order arrived broken was asked for a
   credential they had never been given.

   The real wizard is here now. It is the SAME component the merchant-scoped
   route renders — not a copy — with one extra question at the top, because a
   shopper arriving from Enshield.com has not told us where they bought yet.
   Merchant links still carry that context and still skip straight to the order
   number, and every /file-a-claim/<slug> link already in an email keeps
   working.

   The page's job is to feel trustworthy and calm, because someone reaching it
   has a problem. So the wizard is the first thing on the page, above the fold,
   and everything explanatory sits below it.
   ========================================================================== */
import { CLAIM_STEPS, ClaimSubmission } from '@enshield/public-claims';
import { PageMeta } from '../components/PageMeta';
import { ScrollReveal } from '../components/ScrollReveal';
import { Band, Btn, Card, Grid, SectionHead, Plate } from '../site/primitives';
import { AstronautArt, Checklist, Hero } from '../site/sections';
import { ProtectionPath } from '../site/ProtectionPath';
import { Icon, type IconName } from '../site/icons';
import { ART } from '../site/media';

/*
 * The explanation of the wizard, derived from the wizard.
 *
 * This section listed four steps called Claim details / Evidence / Review /
 * Submit, sitting on the same page as the wizard's own progress line reading
 * "Step 1 of 5" over Verify order / Select items / What happened / Evidence /
 * Review. A shopper was told the count twice and given two different answers,
 * with two different sets of names for the same journey.
 *
 * Keyed off `CLAIM_STEPS` so the two can no longer drift: the labels come from
 * the wizard, and this file only supplies what each one asks of the shopper.
 * `done` is excluded because it is the confirmation, not a step they take.
 */
const STEP_DETAIL: Record<string, { detail: string; icon: IconName }> = {
  verify: { detail: 'Enter your order number and the email you used at checkout.', icon: 'clipboard' },
  items: { detail: 'Choose the items affected, up to the quantity you ordered.', icon: 'store' },
  details: { detail: 'Tell us what happened, in your own words.', icon: 'search' },
  evidence: { detail: 'Add supported photos or PDF documents where they’re relevant.', icon: 'file' },
  review: { detail: 'Check everything, then submit and receive a private tracking token.', icon: 'lock' },
};

const steps = CLAIM_STEPS.filter((step) => step.id !== 'done').map((step) => ({
  label: step.label,
  detail: STEP_DETAIL[step.id]?.detail ?? '',
  icon: STEP_DETAIL[step.id]?.icon ?? ('clipboard' as IconName),
}));

/** Spelled out, so the heading agrees with the wizard's own counter. */
const STEP_COUNT_WORD = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven'][steps.length] ?? String(steps.length);

const assurances: Array<{ icon: IconName; title: string; body: string }> = [
  { icon: 'route', title: 'A clear review path', body: 'Your claim stays connected to the correct order and store from the moment you file it.' },
  { icon: 'lock', title: 'Private tracking', body: 'A private token gives you access to updates after submission — no account needed.' },
  { icon: 'file', title: 'Evidence support', body: 'Supported photos and PDF documents can accompany an active claim.' },
];

/*
 * Same defaults as the merchant-scoped route, read from the same env vars.
 * Duplicated deliberately rather than imported across pages: two small
 * constants are cheaper than a shared module that exists only to hold them.
 */
const supportEmail = import.meta.env.VITE_SUPPORT_EMAIL || 'support@enshield.com';
const publicBaseUrl = import.meta.env.VITE_PUBLIC_BASE_URL || 'https://www.enshield.com';

export function ClaimEntryPage({
  apiClient,
}: {
  apiClient: Parameters<typeof ClaimSubmission>[0]['apiClient'];
}) {
  return (
    <>
      <PageMeta
        title="File a claim"
        description="Start an Enshield claim: find your store, enter your order number and email, and we'll verify your protected purchase."
      />

      <Hero
        compact
        center
        titleId="claim-title"
        eyebrow="File a claim"
        title={<>Let’s get your claim started.</>}
        lead="Enter your order number and the email you used at checkout. We’ll find your protected purchase and guide you through the rest."
        below={
          /*
           * The wizard itself, in the hero.
           *
           * Not a link to the wizard, and not a marketing section standing
           * between the visitor and the form: someone on this page is already
           * having a bad day, and the first thing they should meet is the first
           * question.
           */
          <div className="p-claimsurface p-claimsurface--hero" data-testid="claim-entry-wizard">
            <div className="p-claimcard">
              <div className="p-claimcard__brow">
                <div className="p-claimcard__browtext">
                  <p className="p-claimcard__browtitle">Protected purchase lookup</p>
                  <p className="p-claimcard__browbody">
                    No store to choose and no claim code to find — your order number and email are
                    enough.
                  </p>
                </div>
              </div>
              <ClaimSubmission
                apiClient={apiClient}
                supportEmail={supportEmail}
                publicBaseUrl={publicBaseUrl}
                artworkBaseUrl=""
                // The hero above already carries this page's h1.
                hideHeading
              />
            </div>
          </div>
        }
      />

      {/* Already filed? — the other reason people land here. */}
      <Band space="compact" tone="alt">
        <ScrollReveal className="p-callout">
          <div className="u-stack u-stack--sm">
            <h2 className="u-h3">Already submitted a claim?</h2>
            <p className="u-body">Use the private token from your confirmation to return for updates and add evidence.</p>
          </div>
          <div className="u-cluster">
            <Btn tone="secondary" to="/track-claim">Track your claim</Btn>
          </div>
        </ScrollReveal>
      </Band>

      {/* What happens next. */}
      <Band space="standard" labelledBy="claim-steps-title">
        <div className="u-stack u-stack--xl">
          <ScrollReveal>
            <SectionHead
              align="center"
              eyebrow="What happens next"
              titleId="claim-steps-title"
              title={`${STEP_COUNT_WORD.charAt(0).toUpperCase()}${STEP_COUNT_WORD.slice(1)} steps, and you’re done`}
            />
          </ScrollReveal>
          <ProtectionPath stages={steps} label="Claim filing stages" />
        </div>
      </Band>

      {/* Reassurance. */}
      <Band space="standard" tone="alt" labelledBy="claim-assurance-title">
        <ScrollReveal className="u-stack u-stack--xl">
          <SectionHead align="center" titleId="claim-assurance-title" title="How your claim is handled" />
          <Grid cols={3}>
            {assurances.map((item) => (
              <Card key={item.title}>
                <Plate><Icon name={item.icon} /></Plate>
                <h3 className="u-h4">{item.title}</h3>
                <p className="u-small">{item.body}</p>
              </Card>
            ))}
          </Grid>
        </ScrollReveal>
      </Band>

      {/* Evidence guidance + help. */}
      <Band space="standard" labelledBy="claim-evidence-title">
        <div className="u-split u-split--copy-wide">
          <ScrollReveal className="u-stack u-stack--lg">
            <SectionHead
              eyebrow="Evidence"
              titleId="claim-evidence-title"
              title="What helps a claim get reviewed"
              lead="The claim form accepts supported photos and PDF documents when they’re relevant to what happened."
            />
            <Checklist
              items={[
                'Photos that clearly show the reported issue',
                'Delivery or merchant documents relating to the order',
                'PDF documents within the limits the form displays',
              ]}
            />
            <p className="u-small">File rules come from the Enshield evidence policy and are shown in the claim form as you upload.</p>
          </ScrollReveal>
          <ScrollReveal delay={0.08}>
            <AstronautArt {...ART.evidence} support />
          </ScrollReveal>
        </div>
      </Band>

      <Band space="compact" tone="alt">
        <ScrollReveal className="p-callout">
          <div className="u-stack u-stack--sm">
            <h2 className="u-h3">Need help filing?</h2>
            <p className="u-body">If you can’t find your order number, check the confirmation email from the store you bought from — and if you’re still stuck, contact us.</p>
          </div>
          <div className="u-cluster">
            <Btn tone="secondary" to="/contact">Contact support</Btn>
            <Btn tone="ghost" to="/faq">Claim FAQs</Btn>
          </div>
        </ScrollReveal>
      </Band>
    </>
  );
}
